import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiErrorMessage } from '../../app/api/error';
import agent, { FitmentImportHistoryItem, ImportHistoryStatus } from '../../app/api/agent';
import { choiceValues } from '../../app/components/polaris';
import ImportHistoryCards from './ImportHistoryCards';
import { downloadHandoffMessage, downloadImportFile } from './downloadImportFile';

const pageSize = 20;

type ImportHistoryView = Extract<ImportHistoryStatus, 'active' | 'archived'>;

const historyViewOptions = [
    { value: 'active' as ImportHistoryView, label: 'Active' },
    { value: 'archived' as ImportHistoryView, label: 'Archived' },
];

const ImportHistoryPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const historyKind = searchParams.get('view') === 'exports' ? 'exports' : 'imports';
    const historyNoun = historyKind === 'exports' ? 'export' : 'import';
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const historyRequest = useRef(0);
    const reloadHistory = useRef<() => Promise<void>>(async () => {});
    const [historyViews, setHistoryViews] = useState<ImportHistoryView[]>(['active']);
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [historyErrorTitle, setHistoryErrorTitle] = useState<string | undefined>();
    const [downloadHandedOff, setDownloadHandedOff] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [downloadingReviewIds, setDownloadingReviewIds] = useState<Record<number, boolean>>({});
    const [page, setPage] = useState(1);

    const historyStatus = useMemo<ImportHistoryStatus>(() => {
        const includesActive = historyViews.includes('active');
        const includesArchived = historyViews.includes('archived');

        if (includesActive && includesArchived) {
            return 'all';
        }

        return includesArchived ? 'archived' : 'active';
    }, [historyViews]);

    const loadHistory = useCallback(async () => {
        const request = ++historyRequest.current;
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            // One extra row indicates whether another page exists, without counting every run.
            const result = await agent.Imports.history(historyStatus, {
                kind: historyKind,
                offset: (page - 1) * pageSize,
                limit: pageSize + 1,
            });
            if (request !== historyRequest.current) return;
            setHistory(result);
            if (result.length === 0 && page > 1) setPage((current) => current - 1);
        } catch (error: any) {
            const message = await getApiErrorMessage(error, `Failed to load ${historyNoun} history.`);
            if (request === historyRequest.current) setHistoryError(message);
        } finally {
            if (request === historyRequest.current) setIsLoadingHistory(false);
        }
    }, [historyKind, historyNoun, historyStatus, page]);

    useEffect(() => { setPage(1); }, [historyKind]);

    useEffect(() => {
        reloadHistory.current = loadHistory;
        setHistory([]);
        loadHistory();
        return () => {
            historyRequest.current += 1;
            reloadHistory.current = async () => {};
        };
    }, [loadHistory]);

    const hasNextPage = history.length > pageSize;
    const currentItems = useMemo(() => history.slice(0, pageSize), [history]);

    const pageTitle = historyKind === 'exports' ? 'Export history' : 'Import history';
    const historyTitle = historyStatus === 'all'
        ? `All ${historyKind}`
        : historyStatus === 'archived'
            ? `Archived ${historyKind}`
            : `Active ${historyKind}`;
    const emptyHistoryText = historyStatus === 'archived'
        ? `No archived ${historyKind}.`
        : historyStatus === 'all'
            ? `No ${historyNoun} history yet.`
            : `No active ${historyKind}.`;

    const handleHistoryViewsChange = useCallback((event: Event) => {
        const value = choiceValues(event) as ImportHistoryView[];
        // At least one status stays selected; put the last one back.
        if (value.length === 0) {
            (event.currentTarget as HTMLElement & { values?: string[] }).values = historyViews;
            return;
        }

        setHistoryViews(value);
        setPage(1);
    }, [historyViews]);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            await reloadHistory.current();
        } catch (error: any) {
            setHistoryErrorTitle('Unable to archive history item');
            setHistoryError(await getApiErrorMessage(error, 'Failed to archive history item.'));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            setDownloadHandedOff(false);
            await downloadImportFile(item.queueId);
            setDownloadHandedOff(true);
        } catch (error: any) {
            setHistoryErrorTitle(`Unable to download ${historyNoun} file`);
            setHistoryError(await getApiErrorMessage(error, `Failed to download ${historyNoun} file.`));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, [historyNoun]);

    const handleDownloadReview = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.downloadReview(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            const baseName = (item.originalFileName || `import-${item.queueId}`).replace(/\.[^/.]+$/, '');
            link.href = blobUrl;
            link.download = `${baseName}-review.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download review file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import review file.'));
        } finally {
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const backUrl = historyKind === 'exports' ? '/imports?view=exports' : '/imports';

    return (
        <s-page heading={pageTitle} inlineSize="large">
            <s-link slot="breadcrumb-actions" href={backUrl} onClick={(event) => {
                event.preventDefault();
                navigate(backUrl);
            }}>
                {historyKind === 'exports' ? 'Exports' : 'Imports'}
            </s-link>

            <s-stack gap="base">
                {/* The s-page heading shows only in the admin title bar; repeat it on the page. */}
                <s-heading fontSize="large-200">{pageTitle}</s-heading>

                <s-section accessibilityLabel={pageTitle} padding="none">
                    <s-box padding="base">
                        <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                            <s-stack direction="inline" gap="small-200" alignItems="center">
                                <s-heading>{historyTitle}</s-heading>
                                <s-button variant="tertiary" icon="chevron-down" commandFor="pf-history-status-filter">Status</s-button>
                                <s-popover id="pf-history-status-filter">
                                    <s-box padding="small" minInlineSize="180px">
                                        <s-choice-list
                                            label={historyKind === 'exports' ? 'Export status' : 'Import status'}
                                            labelAccessibilityVisibility="exclusive"
                                            multiple
                                            values={historyViews}
                                            onChange={handleHistoryViewsChange}
                                        >
                                            {historyViewOptions.map(option => (
                                                <s-choice key={option.value} value={option.value}>{option.label}</s-choice>
                                            ))}
                                        </s-choice-list>
                                    </s-box>
                                </s-popover>
                            </s-stack>
                            <s-button variant="tertiary" icon="refresh" onClick={loadHistory} loading={isLoadingHistory}>Refresh</s-button>
                        </s-grid>
                    </s-box>

                    {(downloadHandedOff || historyError) && (
                        <s-box paddingInline="base" paddingBlockEnd="base">
                            <s-stack gap="base">
                                {downloadHandedOff && <s-text color="subdued">{downloadHandoffMessage}</s-text>}
                                {historyError && (
                                    <s-banner tone="critical" heading={historyErrorTitle || `Unable to load ${historyNoun} history`}>
                                        {historyError}
                                    </s-banner>
                                )}
                            </s-stack>
                        </s-box>
                    )}

                    {history.length === 0 && !isLoadingHistory ? (
                        <s-box paddingInline="base" paddingBlockEnd="base">
                            <s-text color="subdued">{emptyHistoryText}</s-text>
                        </s-box>
                    ) : <>
                        <ImportHistoryCards
                            items={currentItems}
                            archivingIds={archivingIds}
                            downloadingIds={downloadingIds}
                            downloadingReviewIds={downloadingReviewIds}
                            onArchive={handleArchive}
                            onDownload={handleDownload}
                            onDownloadReview={handleDownloadReview}
                            onReviewProductTags={item => navigate(`/imports?reviewTagImport=${item.queueId}`)}
                            onDiscardProductTags={item => navigate(`/imports?reviewTagImport=${item.queueId}&discard=true`)}
                        />
                        <s-divider />
                        <s-box padding="small">
                            <s-stack direction="inline" gap="small-200" justifyContent="center">
                                <s-button variant="secondary" icon="chevron-left" accessibilityLabel="Previous"
                                    disabled={isLoadingHistory || page <= 1}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))} />
                                <s-button variant="secondary" icon="chevron-right" accessibilityLabel="Next"
                                    disabled={isLoadingHistory || !hasNextPage}
                                    onClick={() => setPage((prev) => prev + 1)} />
                            </s-stack>
                        </s-box>
                    </>}
                </s-section>
            </s-stack>
        </s-page>
    );
};

export default ImportHistoryPage;
